import { EQuizMode, EQuizStatus } from '@constants';
import { IActivity, IBasicUser, IQuiz, ISection } from '@sdk/contracts';
import { Flex, Table } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import { TFunction } from 'i18next';
import { AlignType, FixedType, ColumnType } from 'rc-table/lib/interface';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Wrapper } from './Quizzes.styles';
import { QuizzesHeader } from './QuizzesHeader';

import { QuizModeBadge } from '@/components/QuizModeBadge';
import { QuizStatusBadge } from '@/components/QuizStatusBadge';
import { QuizzesActions } from '@/components/QuizzesActions';
import { SyncedIcon } from '@/components/SyncedIcon';
import { TableColumnWithTooltip } from '@/components/TableColumnWithTooltip';
import { UserAvatar } from '@/components/UserAvatar';
import { useQuizForm } from '@/hooks';
import { useCourseDetail, useMyQuizzesList, useProfile } from '@/store';

export const Quizzes = () => {
  const {
    isLoading,
    quizzes = [],
    pagination,
    setPageAndLimit,
    setFilter,
    filter,
    setSort
  } = useMyQuizzesList();
  const { t } = useTranslation('CourseTranslations');
  const navigateTo = useNavigate();
  const { setConfigOpen } = useQuizForm();
  const {
    roleCheck: { isManager, isInspector }
  } = useProfile();
  const { courseId } = useCourseDetail();

  useEffect(() => {
    if (!courseId) return;
    setFilter({
      ...filter,
      courses: [courseId]
    });

    return () => {
      setFilter({});
      setSort({});
    };
  }, [courseId, setFilter]);

  const goToQuiz = (quizId: string) => {
    setConfigOpen(false);
    if (isInspector) return navigateTo(`/evaluation/tests/${quizId}`);
  };

  return (
    <Wrapper>
      <QuizzesHeader />
      <Table
        rowKey="_id"
        scroll={{ y: window.innerHeight - 300 }}
        tableLayout="fixed"
        loading={isLoading}
        columns={columns(t, isManager) as ColumnType<IQuiz>[]}
        dataSource={quizzes}
        onRow={quiz => ({
          onClick: () => goToQuiz(quiz._id)
        })}
        onChange={(_, __, sorter) => setSort(sorter as SorterResult<IQuiz>)}
        pagination={{
          pageSize: pagination?.limit,
          total: pagination?.totalDocs,
          showTotal: total => t('QUIZZES_TOTAL_COUNT', { total }),
          onChange: (page, limit) => setPageAndLimit(page, limit)
        }}
      />
    </Wrapper>
  );
};

const columns = (t: TFunction<'CourseTranslations'>, isManager: boolean) => [
  {
    key: 'title',
    title: t('NAME'),
    dataIndex: 'title',
    width: 300,
    ellipsis: true,
    fixed: 'left' as FixedType,
    sorter: true,
    render: (title: string, quiz: IQuiz) => (
      <Flex align="center" gap={8}>
        <span>{title}</span>
        {quiz.parentBaseQuiz && <SyncedIcon />}
      </Flex>
    )
  },
  {
    key: 'status',
    dataIndex: 'status',
    title: t('STATUS'),
    width: 150,
    render: (status: EQuizStatus) => <QuizStatusBadge value={status} />
  },
  {
    key: 'mode',
    dataIndex: 'mode',
    title: t('MODE'),
    align: 'left' as AlignType,
    width: 150,
    render: (mode: EQuizMode) => <QuizModeBadge value={mode} />
  },
  {
    key: 'section',
    title: <TableColumnWithTooltip title={t('SECTION')} tooltip={t('LOCATION_INSIDE_COURSE')} />,
    dataIndex: 'section',
    width: 300,
    ellipsis: true,
    render: (section: ISection) => (section ? <span>{section.name}</span> : '-')
  },
  {
    key: 'activity',
    title: (
      <TableColumnWithTooltip title={t('ACTIVITY')} tooltip={t('ACTIVITY_WHERE_COURSE_IS_USED')} />
    ),
    dataIndex: 'activity',
    width: 200,
    ellipsis: true,
    render: (activity: IActivity) => (activity ? <span>{activity.name}</span> : '-')
  },
  {
    key: 'creator',
    dataIndex: 'owner',
    title: t('CREATOR'),
    align: 'center' as AlignType,
    width: 100,
    render: (owner?: IBasicUser) => <UserAvatar user={owner} />
  },
  ...(isManager
    ? [
        {
          key: 'actions',
          dataIndex: '_id',
          title: t('ACTIONS'),
          width: 100,
          align: 'center' as AlignType,
          fixed: 'right' as FixedType,
          render: (_: string, quiz: IQuiz) => <QuizzesActions quiz={quiz} />
        }
      ]
    : [])
];
